import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import studioApplicationService from '../../../utils/studioApplicationService';

const StudioApplication = ({ userProfile }) => {
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    studioName: '',
    studioWebsite: '',
    studioDescription: '',
    teamSize: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadApplication();
  }, []);

  const loadApplication = async () => {
    setLoading(true);
    const result = await studioApplicationService.getMyApplication();
    if (result.success) {
      setApplication(result.data);
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.studioName || formData.studioName.trim() === '') {
      newErrors.studioName = 'Studio name is required';
    }

    if (formData.studioWebsite && !/^https?:\/\/.+\..+/.test(formData.studioWebsite)) {
      newErrors.studioWebsite = 'Please enter a valid URL';
    }

    if (!formData.studioDescription || formData.studioDescription.trim() === '') {
      newErrors.studioDescription = 'Studio description is required';
    } else if (formData.studioDescription.length < 50) {
      newErrors.studioDescription = 'Please provide at least 50 characters';
    }

    if (!formData.teamSize) {
      newErrors.teamSize = 'Team size is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const result = await studioApplicationService.submitApplication(formData);

      if (result.success) {
        setApplication(result.data);
        setShowForm(false);
        setFormData({
          studioName: '',
          studioWebsite: '',
          studioDescription: '',
          teamSize: ''
        });
      } else {
        setErrors({ submit: result.error });
      }
    } catch (err) {
      setErrors({ submit: 'Failed to submit application. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  // User already has studio_admin role
  if (userProfile?.role === 'studio_admin') {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="CheckCircle" size={24} className="text-green-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              You're a Studio Member!
            </h3>
            <p className="text-muted-foreground mb-4">
              Your account has studio access. You can publish games and access all studio features.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/studio-dashboard')}
              iconName="ArrowRight"
            >
              Go to Studio Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // User has an existing application
  if (application) {
    const statusConfig = {
      pending: {
        icon: 'Clock',
        iconColor: 'text-yellow-500',
        bgColor: 'bg-yellow-500/10',
        title: 'Application Pending',
        description: 'Your studio application is currently under review by our team.',
        action: null
      },
      approved: {
        icon: 'CheckCircle',
        iconColor: 'text-green-500',
        bgColor: 'bg-green-500/10',
        title: 'Application Approved!',
        description: 'Congratulations! Your studio application has been approved. You should now have access to studio features.',
        action: () => navigate('/studio-dashboard')
      },
      rejected: {
        icon: 'XCircle',
        iconColor: 'text-red-500',
        bgColor: 'bg-red-500/10',
        title: 'Application Not Approved',
        description: application.rejection_reason || 'Unfortunately, your studio application was not approved at this time.',
        action: null
      }
    };

    const config = statusConfig[application.status];

    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
            <Icon name={config.icon} size={24} className={config.iconColor} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {config.title}
            </h3>
            <p className="text-muted-foreground mb-4">
              {config.description}
            </p>

            <div className="bg-muted/30 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-medium text-foreground mb-3">Application Details</h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Studio Name:</dt>
                  <dd className="font-medium text-foreground">{application.studio_name}</dd>
                </div>
                {application.studio_website && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Website:</dt>
                    <dd className="font-medium text-primary">
                      <a href={application.studio_website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {application.studio_website}
                      </a>
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Team Size:</dt>
                  <dd className="font-medium text-foreground capitalize">{application.team_size?.replace('-', ' to ')}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Submitted:</dt>
                  <dd className="font-medium text-foreground">
                    {new Date(application.created_at).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            {config.action && (
              <Button
                variant="default"
                onClick={config.action}
                iconName="ArrowRight"
              >
                Go to Studio Dashboard
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No application - show option to apply
  if (!showForm) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="Gamepad2" size={24} className="text-blue-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Become a Studio Developer
            </h3>
            <p className="text-muted-foreground mb-4">
              Are you a game developer or studio? Apply to publish games on Solis Games and reach thousands of players.
            </p>

            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Studio Benefits:
              </h4>
              <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={16} />
                  Publish and manage your games
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={16} />
                  Access SDK and API for integration
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={16} />
                  Revenue sharing program (70% to you)
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={16} />
                  Detailed analytics and reporting
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={16} />
                  Dedicated developer support
                </li>
              </ul>
            </div>

            <Button
              variant="default"
              onClick={() => setShowForm(true)}
              iconName="Plus"
            >
              Apply Now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show application form
  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Studio Application
        </h3>
        <p className="text-muted-foreground">
          Tell us about your studio and we'll review your application within 2-3 business days.
        </p>
      </div>

      {errors.submit && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={20} className="text-destructive" />
            <span className="text-sm text-destructive">{errors.submit}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Studio Name"
          type="text"
          placeholder="Enter your studio name"
          value={formData.studioName}
          onChange={(e) => handleInputChange('studioName', e.target.value)}
          error={errors.studioName}
          required
          disabled={submitting}
        />

        <Input
          label="Studio Website"
          type="url"
          placeholder="https://yourstudio.com"
          value={formData.studioWebsite}
          onChange={(e) => handleInputChange('studioWebsite', e.target.value)}
          error={errors.studioWebsite}
          description="Optional - Your studio's official website"
          disabled={submitting}
        />

        <div>
          <label className="text-sm font-medium text-foreground block mb-2">
            Studio Description <span className="text-destructive">*</span>
          </label>
          <textarea
            placeholder="Tell us about your studio and the games you develop... (minimum 50 characters)"
            value={formData.studioDescription}
            onChange={(e) => handleInputChange('studioDescription', e.target.value)}
            className="flex min-h-[120px] w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={submitting}
            rows={5}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.studioDescription && (
              <p className="text-sm text-destructive">{errors.studioDescription}</p>
            )}
            <p className="text-xs text-muted-foreground ml-auto">
              {formData.studioDescription.length} / 50 characters minimum
            </p>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground block mb-2">
            Team Size <span className="text-destructive">*</span>
          </label>
          <select
            value={formData.teamSize}
            onChange={(e) => handleInputChange('teamSize', e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={submitting}
          >
            <option value="">Select team size</option>
            <option value="solo">Solo Developer</option>
            <option value="2-5">2-5 people</option>
            <option value="6-10">6-10 people</option>
            <option value="11-50">11-50 people</option>
            <option value="51+">51+ people</option>
          </select>
          {errors.teamSize && (
            <p className="text-sm text-destructive mt-1">{errors.teamSize}</p>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowForm(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            loading={submitting}
            disabled={submitting}
            iconName="Send"
          >
            Submit Application
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StudioApplication;
